import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NgSelectComponent} from '@ng-select/ng-select';
import {SignalService} from '@services/administration/machine/signal.service';
import {UtilsService} from '@services/utils/utils.service';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {DATA_TYPE} from '@constants/data-type.constant';
import {Signal} from '@models/dashboard/signal';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {BIGQUERY_VALID_NAME_REGEX} from '@constants/reg-exp';

@Component({
    selector: 'scitis-admin-calculated-signal-edit-modal',
    templateUrl: './calculated-signal-edit-modal.component.html',
    styleUrls: ['./calculated-signal-edit-modal.component.scss'],
    standalone: false
})
export class CalculatedSignalEditModalComponent implements OnInit, OnDestroy {


  @Input()
  signal: Signal = new Signal('', '', '', '');

  @Input()
  machineId: number = -1;

  @Input()
  organizationId: number = -1;

  @Input()
  allSignals: Signal[] = [];

  @Input()
  applyToMachinesOfSameTag: boolean = false;
  @Input()
  applyToMachinesOfSameTagMessage: string = '';

  @Input()
  isHigherThanAdmin: boolean = false;

  isSignalsCollapsed = true;
  isConstantsCollapsed = true;

  isNewSignal: boolean = false;
  isLoadingSignals: boolean = false;
  isLoadingConstants: boolean = false;
  submitted: boolean = false;
  signalNameUsed: boolean = false;
  signalLabelUsed: boolean = false;

  signals: any[] = new Array<any>();
  usableSignals: {name: string, label: string}[] = [];
  tenantConstants: string[] = new Array<string>();
  usablePortalSettings: any[] = new Array<any>();
  allConstants: any[] = new Array<any>();
  cursorPosition: number = 0;

  selectedFormulaConstant: string = '';

  @ViewChild('selectSignal')
  ngSelectSignal: NgSelectComponent | any;

  @ViewChild('selectConstant')
  ngSelectConstant: NgSelectComponent | any;

  color: string = '';

  ALLOWED_SIGNAL_TYPES = [String(DATA_TYPE.INTEGER), String(DATA_TYPE.FLOAT), String(DATA_TYPE.FLOAT64), String(DATA_TYPE.INT64)];
  SIGNAL_SYMBOL_PREFIX = '$';
  PLACEHOLDER_DELIMITER_START = '{';
  PLACEHOLDER_DELIMITER_END = '}';
  TENANT_SPECIFIC_CONSTANT_SYMBOL_PREFIX = '#';
  DEFAULT_COLOR = '#CCC';


  signalForm: UntypedFormGroup = new UntypedFormGroup({});
  private destroy: Subject<boolean> = new Subject<boolean>();

  constructor(
      private signalService: SignalService,
      private activeModal: NgbActiveModal,
      private utilsService: UtilsService) {
  }

  get f() {
    return this.signalForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    return !this.isHigherThanAdmin || (this.submitted ? this.signalForm.invalid : this.submitted && this.signalForm.invalid);
  };

  private static getCurrentUTCTimestamp() {
    const date = new Date();
    const timezoneOffset = date.getTimezoneOffset();
    date.setMinutes(date.getMinutes() + timezoneOffset);
    return date.getTime();
  }

  private static isSignalInSignalList(items: any, itemName: string) {
    if (!items || items.length <= 0) {
      return false;
    }
    for (const value of items) {
      if (value.name === itemName) {
        return true;
      }
    }
    return false;
  }

  private static findItemIndexInList(items: any[], itemId: number) {
    const defaultIndex = -1;
    if (!items || items.length <= 0) {
      return defaultIndex;
    }
    for (let index = 0; index < items.length; index++) {
      const existing = items[index];
      if (existing.id === itemId) {
        return index;
      }
    }
    return defaultIndex;
  }

  ngOnInit(): void {
    this.signal = this.utilsService.getObjectDeepCopy(this.signal);
    if (!this.signal || this.signal.id <= 0) {
      this.isNewSignal = true;
      this.signal.id = -1;
    }
    if (this.signal.signalColor == null || !this.signal.signalColor) {
      this.signal.signalColor = this.DEFAULT_COLOR;
    } else {
      this.color = this.signal.signalColor;
    }
    this.setPossibleSignals(this.allSignals);
    this.setConstants();
    this.generateSignalForm(this.signal);
  }

  onAddSelectedSignal(selected: any) {
    const signalName = selected.name;
    if (!this.signal.signals) {
      this.signal.signals = [];
    }

    const newSignalPlaceholder = this.generateSignalPlaceholderName(signalName);
    this.updateFormula(newSignalPlaceholder);

    if (!CalculatedSignalEditModalComponent.isSignalInSignalList(this.signal.signals, signalName)) {
      this.pushSignalToSignalsList(signalName);
    }

    this.ngSelectSignal.itemsList.clearSelected(true);
  }

  onRemoveSignal(signal: Signal) {
    if (this.signal.signals) {
      const index = CalculatedSignalEditModalComponent.findItemIndexInList(this.signal.signals, signal.id);
      if (index > -1) {
        this.signal.signals.splice(index, 1);
      }
      const signalToRemovePlaceholder = this.generateSignalPlaceholderName(signal.name);
      this.signal.formula = this.signal.formula.replaceAll(signalToRemovePlaceholder, '');
      this.signalForm.controls.formula.setValue(this.signal.formula);
    }
  }

  onAddSelectedTenantConstant(selected: any) {
    if (!this.signal.tenantConstants) {
      this.signal.tenantConstants = [];
    }
    const constantPlaceholder = this.generateConstantPlaceholderName(selected);
    this.updateFormula(constantPlaceholder);

    if (!this.isConstantInTenantConstants(selected)) {
      this.setConstantToSignalsTenantConstants(selected);
    }
    this.selectedFormulaConstant = '';
    this.ngSelectConstant.itemsList.clearSelected(true);
  }

  getConstantActiveValue(constant: any) {
    let value = '';
    const timestamp = CalculatedSignalEditModalComponent.getCurrentUTCTimestamp();
    const possibleValues = constant.constantValues;
    for (const val of possibleValues) {
      if (val.startTimestamp <= timestamp && val.endTimestamp >= timestamp) {
        value = val.value;
        break;
      }
    }
    return value;
  }

  getNameForSettingId(settingId: any) {
    for (const setting of this.usablePortalSettings) {
      if (setting.id === settingId) {
        return setting.name;
      }
    }
  }

  getValueForSettingId(settingId: any) {
    for (const setting of this.usablePortalSettings) {
      if (setting.id === settingId) {
        return setting.value;
      }
    }
  }

  onRemoveConstant(constant: any) {
    if (this.signal.tenantConstants) {
      const index = CalculatedSignalEditModalComponent.findItemIndexInList(this.signal.tenantConstants, constant.id);
      if (index > -1) {
        this.signal.tenantConstants.splice(index, 1);
      }
      const name = this.getNameForSettingId(constant.settingId);
      const constantPlaceholderToRemove = this.generateConstantPlaceholderName(name);
      this.signal.formula = this.signal.formula.replace(new RegExp(constantPlaceholderToRemove, 'g'), '');
      this.signalForm.controls.formula.setValue(this.signal.formula);
    }
  }

  onClose() {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel() {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onCloseColorPicker($event: string) {
    this.color = $event;
    this.signalForm.controls.color.setValue($event);
  }

  onSave() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.signalForm.invalid || this.signalNameUsed || this.signalLabelUsed) {
      return;
    }
    this.updateSignalProperties();
    this.signal.name = this.signal.name.trim();
    this.signal.signal = this.signal.signal.trim();
    this.signal.label = this.signal.label.trim();
    this.signal.formula = this.signal.formula.trim();
    this.activeModal.close(this.signal);
  }

  signalNameChanged(): void {
    this.signal.name = this.signalForm.controls.signalName.value;
    this.signalForm.controls.signalLabel.setValue(this.signalForm.controls.signalName.value);
    this.signalLabelChanged();
    this.signalNameUsed = false;
    for (const signal of this.allSignals) {
      if (signal.name === this.signal.name.trim()) {
        this.signalNameUsed = true;
        return;
      }
    }
  }

  signalLabelChanged(): void {
    this.signal.label = this.signalForm.controls.signalLabel.value;
    this.signalLabelUsed = this.signalService.signalLabelAlreadyUsed(this.allSignals, this.signal);
  }

  updateFormula(symbol: string) {
    if (this.signal.formula) {
      this.signal.formula =
          this.signal.formula?.substring(0, this.cursorPosition)
          + symbol
          + this.signal.formula?.substring(this.cursorPosition);
    } else {
      this.signal.formula = symbol;
    }

    this.signalForm.controls.formula.setValue(this.signal.formula);
    this.cursorPosition += symbol.length;
  }

  signalPrecisionChanged(): void {
    this.signal.decimals = this.signalForm.controls.decimals.value;
  }

  signalUnitChanged(): void {
    this.signal.unit = this.signalForm.controls.unit.value;
  }

  streamToBigQueryChanged(): void {
    this.signal.streamToBigQuery = !this.signal.streamToBigQuery;
  }

  formulaChanged(): void {
    this.signal.formula = this.signalForm.controls.formula.value;
    this.updateCursorPosition();
  }

  updateCursorPosition() {
    const textAreaElement: any = document.getElementById('formula');
    this.cursorPosition = textAreaElement?.selectionStart;
  }

  clearAll() {
    this.signalForm.controls.formula.reset();
    this.signal.formula = '';
    this.signal.signals = [];
    this.signal.tenantConstants = [];
  }

  searchSignal(searchTerm: string, item: any) {
    const lowerCasedTerm = searchTerm.toLowerCase();
    return (
        item.name.toLowerCase().includes(lowerCasedTerm) ||
        item.label.toLowerCase().includes(lowerCasedTerm)
    );
  }

  private setPossibleSignals(signals: Signal[]) {
    this.isLoadingSignals = true;
    this.usableSignals = [];
    for (const currentSignal of signals) {
      if (currentSignal) {
        if ((!currentSignal.calculated && this.ALLOWED_SIGNAL_TYPES.indexOf(currentSignal.type) > -1)) {
          const nameAndLabel = {name: currentSignal.name, label: currentSignal.label};
          this.usableSignals.push(nameAndLabel);
        }
      }
    }
    this.isLoadingSignals = false;
  }

  private setConstants() {
    this.isLoadingConstants = true;
    this.signalService.listConstants(this.organizationId).pipe(takeUntil(this.destroy)).subscribe({
      next: (result) => {
        for (const element of result) {
          this.tenantConstants = [...this.tenantConstants, element.portalSetting.name];
          this.usablePortalSettings.push(element.portalSetting);
          this.allConstants.push(element.tenantConstant);
        }
        this.isLoadingConstants = false;
      }, error: (error) => {
        console.log('error: ' + error);
        this.isLoadingConstants = false;
      }
    });
  }

  private generateSignalForm(signal: Signal) {
    this.signalForm = new UntypedFormGroup({
      signalName: new UntypedFormControl(signal.name, [Validators.pattern(BIGQUERY_VALID_NAME_REGEX), Validators.required]),
      signalLabel: new UntypedFormControl(signal.label, [Validators.required]),
      signal: new UntypedFormControl(null),
      tenantConstant: new UntypedFormControl(null),
      unit: new UntypedFormControl(signal.unit),
      decimals: new UntypedFormControl(signal.decimals, [Validators.min(0), Validators.max(9)]),
      color: new UntypedFormControl(signal.signalColor),
      formula: new UntypedFormControl(signal.formula, [Validators.required]),
    });

    if (!this.isHigherThanAdmin) {
      this.signalForm.disable();
    }
  }

  private isConstantInTenantConstants(selected: any) {
    const id = this.getSettingIdForSettingName(selected);
    for (const constant of this.signal.tenantConstants) {
      if (constant.settingId === id) {
        return true;
      }
    }
    return false;
  }

  private getSettingIdForSettingName(settingName: any) {
    for (const setting of this.usablePortalSettings) {
      if (setting.name === settingName) {
        return setting.id;
      }
    }
  }

  private setConstantToSignalsTenantConstants(selected) {
    const id = this.getSettingIdForSettingName(selected);
    for (const constant of this.allConstants) {
      if (constant.settingId === id) {
        this.signal.tenantConstants.push(constant);
      }
    }
  }

  private updateSignalProperties() {
    this.signal.calculated = true;
    this.signal.name = this.f.signalName.value;
    this.signal.label = this.f.signalLabel.value;
    this.signal.signal = this.f.signalName.value;
    this.signal.formula = this.f.formula.value;
    this.signal.signalColor = this.color;
    this.signal.parentId = this.machineId;
    if (this.f.unit.value != undefined) {
      this.signal.unit = this.f.unit.value;
    }
    if (this.f.decimals.value) {
      this.signal.decimals = this.f.decimals.value;
    } else {
      this.signal.decimals = 0;
    }
  }

  private generateSignalPlaceholderName(name: string) {
    return this.SIGNAL_SYMBOL_PREFIX + this.PLACEHOLDER_DELIMITER_START + name +
        this.PLACEHOLDER_DELIMITER_END;
  }

  private pushSignalToSignalsList(signalName: string) {
    for (const signal of this.allSignals) {
      if (signal.name === signalName) {
        this.signal.signals.push(signal);
      }
    }
  }

  private generateConstantPlaceholderName(constantName: string) {
    return this.TENANT_SPECIFIC_CONSTANT_SYMBOL_PREFIX + this.PLACEHOLDER_DELIMITER_START +
        constantName + this.PLACEHOLDER_DELIMITER_END;
  }

  ngOnDestroy() {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
